import { useEffect, useState } from "react";
import { usePage } from "@inertiajs/react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Check, AlertTriangle, X } from "lucide-react";
import ContactText from "@/components/ContactText";

const AUTO_DISMISS_MS = 4000;

/**
 * Renders the server-side flash messages set via Rails `flash[:notice]` or
 * `flash[:alert]`. Appears fixed at the top-center of the viewport and
 * auto-dismisses after a few seconds. Inertia passes the flash hash via
 * shared props (see ApplicationController). Mounted once for every page
 * in lib/inertiaApp.jsx.
 *
 * When the server sets flash[:contact_subject], "Contact us" in the message
 * becomes a mailto link with that subject, and the toast stays until closed
 * so there's time to click it.
 */
export default function Flash({ skipAlerts = false }) {
  const { flash } = usePage().props;
  const alert = skipAlerts ? null : flash?.alert;
  const message = alert || flash?.notice;
  const isAlert = !!alert;
  const contactSubject = flash?.contact_subject;
  const sticky = !!contactSubject && /contact us/i.test(message || "");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!message) {
      setVisible(false);
      return;
    }
    setVisible(true);
    if (sticky) return;
    const timer = setTimeout(() => setVisible(false), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
    // Keyed on the flash itself, not just its text: pages stay mounted
    // across visits, so saving twice brings the same message back.
  }, [message, flash, sticky]);

  if (!message || !visible) return null;

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[90%] max-w-md animate-in fade-in slide-in-from-top-2 duration-200">
      {/* Override the variant's tinted bg with a solid card surface so
          the toast reads opaque over any page content. Keeps the
          variant's coloured border + text as the success/error signal. */}
      <Alert
        variant={isAlert ? "destructive" : "success"}
        className="bg-card shadow-lg"
      >
        {/* Before the icon so Alert's [&>svg~*] padding doesn't apply to it. */}
        {sticky && (
          <button
            type="button"
            onClick={() => setVisible(false)}
            className="absolute right-3 top-3 rounded p-0.5 text-muted-foreground hover:text-foreground"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {isAlert ? <AlertTriangle className="h-4 w-4" /> : <Check className="h-4 w-4" />}
        <AlertDescription className={sticky ? "pr-6" : undefined}>
          <ContactText text={message} subject={contactSubject} />
        </AlertDescription>
      </Alert>
    </div>
  );
}
